export type ServiceStatus = 'full' | 'partial' | 'none'

export type DateGranularity = 'daily' | 'weekly' | 'custom'

/**
 * Daily and weekly derive their span from the anchor alone; `custom` is a
 * chef-picked window (PROD-1160 — a kitchen with two or three days of cold
 * storage buys in short cycles and can't take a whole week at once), so it
 * carries its own inclusive end.
 *
 * A discriminated union rather than an optional `end`: the gestures that jump
 * to a date reuse the CURRENT granularity, and with an optional field they
 * would silently build an end-less custom range. Here the compiler names every
 * one of them — see `reanchorRange`, which is the fix they all want.
 */
export type DateRange =
  | {
      granularity: 'daily' | 'weekly'
      /**
       * Daily: the specific day shown. Weekly: any day inside the target week —
       * the range builder snaps to that week's Monday.
       */
      anchor: Date
    }
  | {
      granularity: 'custom'
      /** First day of the window. */
      anchor: Date
      /** Last day of the window, INCLUSIVE. */
      end: Date
    }

/**
 * Longest window the custom picker will hand back. The aggregation server caps
 * at 366 days (`SHOPPING_LIST_MAX_DAYS`); 90 is the interactive ceiling Plan 17
 * set for the in-page table, and the number that manager's docblock already
 * claims the chef UI enforces.
 */
export const CUSTOM_RANGE_MAX_DAYS = 90

/** What the Custom pill opens on — the short buying cycle it exists for. */
export const CUSTOM_RANGE_DEFAULT_DAYS = 3

export interface ServiceDay<C = unknown> {
  date: Date
  weekday: string
  label: string
  status: ServiceStatus
  offCampuses: C[]
}

const WEEKDAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * Monday of the service week containing `today`. On weekends this is the
 * UPCOMING Mon–Fri week — "this week" at a Saturday kitchen table means the
 * week being planned, not the one that just ended (matches `buildServiceWeek`
 * on the family surface).
 */
export function startOfWorkWeek(today: Date): Date {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const day = d.getDay()
  const diffToMon = day === 0 ? 1 : day === 6 ? 2 : 1 - day
  d.setDate(d.getDate() + diffToMon)
  return d
}

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

export function addDays(d: Date, days: number): Date {
  const out = new Date(d)
  out.setDate(out.getDate() + days)
  return out
}

export function dayDiff(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / (24 * 60 * 60 * 1000))
}

export function isoDate(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Parse a `YYYY-MM-DD` calendar key into a LOCAL Date — the inverse of
 * `isoDate`. Never use `new Date(key)` for this: that parses the bare form as
 * UTC midnight, which lands on the previous day in every US timezone.
 */
export function localDateFromKey(key: string): Date {
  const [y, mo, d] = key.split('-').map(Number)
  return new Date(y ?? 1970, (mo ?? 1) - 1, d ?? 1)
}

/**
 * True for a `YYYY-MM-DD` that names a real calendar day. Round-tripping
 * through the local-date constructor rejects overflow forms like `2026-02-30`,
 * which `Date` would otherwise silently roll forward to March 2nd.
 */
export function isValidDateKey(key: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    return false
  }
  return isoDate(localDateFromKey(key)) === key
}

/**
 * The inclusive [start, end] a range covers. The single place a granularity
 * becomes concrete days: `buildServiceDays` renders these and both chef pages
 * send them to the server as `start`/`end`, so the window fetched and the
 * window drawn cannot drift apart — they were two hand-kept-in-sync copies
 * before this.
 */
export function rangeWindow(range: DateRange): { start: Date; end: Date } {
  if (range.granularity === 'custom') {
    return { start: startOfDay(range.anchor), end: startOfDay(range.end) }
  }
  if (range.granularity === 'daily') {
    const day = startOfDay(range.anchor)
    return { start: day, end: day }
  }
  const start = startOfWorkWeek(range.anchor)
  return { start, end: addDays(start, 4) }
}

/** Inclusive day count of the window a range covers. */
export function rangeDayCount(range: DateRange): number {
  const { start, end } = rangeWindow(range)
  return dayDiff(start, end) + 1
}

/**
 * The service weeks a range touches, as Monday `YYYY-MM-DD` keys in order.
 *
 * Weekend days are SKIPPED rather than mapped: `startOfWorkWeek` deliberately
 * rolls a weekend forward to the upcoming Monday — right for "which week am I
 * planning today", wrong for "which week does this service day belong to", and
 * a custom window is the first granularity whose days can land on a weekend at
 * all. Without the skip, a window ending on a Saturday would claim a week it
 * never covers. An all-weekend window touches no service week, which is
 * correct: nothing is served, so there is nothing to order.
 */
export function rangeServiceWeekKeys(range: DateRange): string[] {
  const { start } = rangeWindow(range)
  const keys: string[] = []
  for (let i = 0; i < rangeDayCount(range); i++) {
    const date = addDays(start, i)
    const weekday = date.getDay()
    if (weekday === 0 || weekday === 6) {
      continue
    }
    const key = isoDate(startOfWorkWeek(date))
    if (!keys.includes(key)) {
      keys.push(key)
    }
  }
  return keys
}

/**
 * Build a custom range from two `YYYY-MM-DD` keys, or null when the pair names
 * no window: a malformed key, or an end before the start. An out-of-order pair
 * is deliberately NOT swapped — silently reordering a chef's typo hides it.
 *
 * An over-long span IS clamped rather than rejected, because the two ways to
 * produce one (dragging the end date out, following a hand-edited link) both
 * want the longest legal window, not a dead control.
 */
export function customRangeFromKeys(startKey: string, endKey: string): DateRange | null {
  if (!isValidDateKey(startKey) || !isValidDateKey(endKey)) {
    return null
  }
  const anchor = localDateFromKey(startKey)
  const span = dayDiff(anchor, localDateFromKey(endKey))
  if (span < 0) {
    return null
  }
  return { granularity: 'custom', anchor, end: addDays(anchor, Math.min(span, CUSTOM_RANGE_MAX_DAYS - 1)) }
}

/**
 * Move a range so its window starts on `anchor`, keeping a custom window's
 * length. Every "jump to this date" gesture (month-calendar click, "go to next
 * day with orders", the stepper arrows) reuses the current granularity, and a
 * custom range has to carry its end along or it can't be rebuilt.
 */
export function reanchorRange(range: DateRange, anchor: Date): DateRange {
  if (range.granularity !== 'custom') {
    return { granularity: range.granularity, anchor }
  }
  const start = startOfDay(anchor)
  return { granularity: 'custom', anchor: start, end: addDays(start, rangeDayCount(range) - 1) }
}

/**
 * The range a granularity switch lands on: anchored to today's day/week so the
 * new mode starts on a familiar position rather than wherever the previous
 * anchor happened to sit. Doubles as the cold-start default builder for a
 * surface's preferred granularity, so "what the page opens on" and "what a
 * toggle does" cannot drift apart.
 */
export function rangeForGranularityToggle(next: DateGranularity, today: Date): DateRange {
  if (next === 'custom') {
    const start = startOfDay(today)
    return { granularity: 'custom', anchor: start, end: addDays(start, CUSTOM_RANGE_DEFAULT_DAYS - 1) }
  }
  return { granularity: next, anchor: next === 'daily' ? startOfDay(today) : startOfWorkWeek(today) }
}

/**
 * Roll up per-campus availability for a date.
 * `full` = all campuses serving, `none` = all closed, `partial` = some open / some closed.
 */
export function computeServiceStatus<C extends { id: string }>(
  date: Date,
  campuses: C[],
  offWeekdaysByCampus: Record<string, number[] | undefined>,
  holidayDates: Set<string>,
): { status: ServiceStatus; offCampuses: C[] } {
  const day = date.getDay()
  const isHoliday = holidayDates.has(isoDate(date))
  const offCampuses = campuses.filter((c) => {
    if (isHoliday) {
      return true
    }
    if (day === 0 || day === 6) {
      return true
    }
    const offDays = offWeekdaysByCampus[c.id] ?? []
    return offDays.includes(day)
  })
  if (offCampuses.length === 0) {
    return { status: 'full', offCampuses: [] }
  }
  if (offCampuses.length === campuses.length) {
    return { status: 'none', offCampuses }
  }
  return { status: 'partial', offCampuses }
}

export function buildServiceDays<C>(
  range: DateRange,
  getStatus: (date: Date) => { status: ServiceStatus; offCampuses: C[] },
): ServiceDay<C>[] {
  const { start } = rangeWindow(range)
  const dates: Date[] = []
  for (let i = 0; i < rangeDayCount(range); i++) {
    dates.push(addDays(start, i))
  }
  return dates.map((date) => {
    const { status, offCampuses } = getStatus(date)
    return {
      date,
      weekday: WEEKDAY_FULL[date.getDay()] ?? '',
      label: `${date.getMonth() + 1}/${date.getDate()}`,
      status,
      offCampuses,
    }
  })
}

/**
 * Orders are placed on the Thursday of the week BEFORE the service week, so a
 * given service date D is "predicted" only while `today` is still before the
 * Thursday immediately preceding the Monday of D's week. Once that Thursday
 * passes, the orders for the entire next week are locked in and the totals
 * are actual.
 */
export function predictedCutoff(range: DateRange): Date {
  // The LAST service week the window touches. Daily and weekly live in exactly
  // one week, so this is the same cutoff they always computed; a custom window
  // can span several, and its numbers stay provisional while the latest of them
  // is still pre-cutoff. Falls back to the anchor for an all-weekend window,
  // which names no service week.
  const weekKeys = rangeServiceWeekKeys(range)
  const lastKey = weekKeys[weekKeys.length - 1]
  const weekMonday = lastKey ? localDateFromKey(lastKey) : startOfWorkWeek(range.anchor)
  return addDays(weekMonday, -4)
}

/**
 * True when the selected range is still pre-cutoff and the totals shown are
 * estimates rather than placed orders.
 */
export function isPredictedRange(range: DateRange, today: Date): boolean {
  return startOfDay(today).getTime() < predictedCutoff(range).getTime()
}

/**
 * Whether to show the "provisional numbers" banner: we always work with the
 * actual placed orders (no preview/estimate), so the banner appears only when
 * real orders exist AND the ordering cutoff hasn't passed yet (quantities may
 * still change). After the cutoff the numbers are final and no banner shows.
 */
export function shouldShowProvisionalBanner(range: DateRange, today: Date, hasPlacedOrders: boolean): boolean {
  return hasPlacedOrders && isPredictedRange(range, today)
}

export function formatRangeLabel(range: DateRange, today: Date): string {
  if (range.granularity === 'custom') {
    // No "This Week"-style shorthand: the whole point of a custom window is
    // that it doesn't line up with a named period, so it names its own days.
    const startMonth = range.anchor.toLocaleDateString('en-US', { month: 'short' })
    const sameMonth =
      range.anchor.getMonth() === range.end.getMonth() && range.anchor.getFullYear() === range.end.getFullYear()
    if (sameMonth) {
      // From == To is a legal pick (the form offers "1 day"), and it names a
      // single date, not the degenerate range "Aug 14–14".
      return range.anchor.getDate() === range.end.getDate()
        ? `${startMonth} ${range.anchor.getDate()}`
        : `${startMonth} ${range.anchor.getDate()}–${range.end.getDate()}`
    }
    const endMonth = range.end.toLocaleDateString('en-US', { month: 'short' })
    return `${startMonth} ${range.anchor.getDate()} – ${endMonth} ${range.end.getDate()}`
  }
  if (range.granularity === 'daily') {
    const diff = dayDiff(today, range.anchor)
    if (diff === 0) {
      return 'Today'
    }
    if (diff === 1) {
      return 'Tomorrow'
    }
    if (diff === -1) {
      return 'Yesterday'
    }
    const month = range.anchor.toLocaleDateString('en-US', { month: 'short' })
    return `${WEEKDAY_FULL[range.anchor.getDay()]?.slice(0, 3) ?? ''} ${month} ${range.anchor.getDate()}`
  }
  const todayWeek = startOfWorkWeek(today)
  const anchorWeek = startOfWorkWeek(range.anchor)
  const weeksFromToday = Math.round(dayDiff(todayWeek, anchorWeek) / 7)
  if (weeksFromToday === 0) {
    return 'This Week'
  }
  if (weeksFromToday === 1) {
    return 'Next Week'
  }
  if (weeksFromToday === -1) {
    return 'Last Week'
  }
  const month = anchorWeek.toLocaleDateString('en-US', { month: 'short' })
  return `Week of ${month} ${anchorWeek.getDate()}`
}

/**
 * Day label for the daily empty state ("No orders … for {label}"). Within a
 * direct week (this / last / next) the weekday alone is unambiguous ("Tuesday");
 * further out we append the day-of-month so it's clear which one ("Tuesday 21").
 * Returns `undefined` for the weekly view (no single day to name).
 */
export function dailyEmptyDayLabel(range: DateRange, today: Date): string | undefined {
  if (range.granularity !== 'daily') {
    return undefined
  }
  const date = range.anchor
  const weekday = WEEKDAY_FULL[date.getDay()] ?? ''
  const weeksFromToday = Math.round(dayDiff(startOfWorkWeek(today), startOfWorkWeek(date)) / 7)
  return Math.abs(weeksFromToday) <= 1 ? weekday : `${weekday} ${date.getDate()}`
}

/** Pretty range for the predicted-mode alert ("Orders for {this week} ..."). */
export function formatRangeForCopy(range: DateRange, today: Date): string {
  const label = formatRangeLabel(range, today)
  if (label === 'Today' || label === 'Tomorrow' || label === 'Yesterday') {
    return label.toLowerCase()
  }
  if (label === 'This Week' || label === 'Next Week' || label === 'Last Week') {
    return label.toLowerCase()
  }
  return label
}
